<template>
  <div class="step-filter-config" data-testid="step-filter-config__view">

    <!-- Toolbar -->
    <div class="d-flex align-center justify-end mb-4 mt-2">
      <v-btn
        v-if="hasActiveTableFilter"
        outlined
        small
        class="mr-2"
        color="primary"
        @click="clearTableFilter"
        data-testid="step-filter-config__clear-filter-btn"
      >
        <v-icon left small>mdi-filter-remove</v-icon>
        <span>Clear Filter</span>
      </v-btn>
      <base-button
        v-if="formatedData.length > 0"
        primary
        label="Save Order"
        class="mr-2"
        :disabled="!isDirty"
        data-testid="step-filter-config__save-btn"
        @click="saveFilters"
      />
      <base-button
        primary
        label="Filter"
        prepend-icon="mdi-plus"
        data-testid="step-filter-config__create-btn"
        @click="openCreateDialog"
      />
    </div>

    <!-- Filters Table -->
    <lb-datatableaction
      ref="dataTableAction"
      :headers="HEADERS"
      :tabledata="mappedTableData"
      :bulkActions="false"
      :showToolbar="false"
      data-testid="step-filter-config__table"
      @filter-item="filterData"
      @clear-item="clearFilter"
    >
      <template v-slot:tbody>
        <tr
          v-for="(item, i) in filteredTableData"
          :key="i"
          draggable="true"
          class="step-filter-config__row cursor-grab"
          :class="{ 'low-opacity': !isFilterEnabled(item) }"
          data-testid="step-filter-config__table-row"
          @dragstart="onDragStart(i)"
          @dragover.prevent
          @drop="onDropFilter(i)"
          @dragend="onDragEnd"
        >
          <td class="px-2" style="width:36px" data-testid="step-filter-config__col-drag">
            <img src="/img/icons/tm-icons/DragIcon.svg" alt="">
          </td>
          <td class="px-3 text-center body-2" data-testid="step-filter-config__col-index">
            {{ item.indexno + 1 }}
          </td>
          <td class="px-3 body-2" data-testid="step-filter-config__col-filetype">
            {{ getItemFileTypeName(item) }}
          </td>
          <td class="px-3 body-2" data-testid="step-filter-config__col-tag">
              {{ item.tag }}
          </td>
          <td class="px-3 body-2" data-testid="step-filter-config__col-description">
            {{ item.description }}
          </td>
          <td class="px-3 body-2" data-testid="step-filter-config__col-excludetype">
            {{ item.excludetype }}
          </td>
          <td class="px-3 body-2" data-testid="step-filter-config__col-table">
            {{ getTableName(item.tableid) }}
          </td>
          <td class="px-3 body-2" data-testid="step-filter-config__col-column">
            {{ item.column }}
          </td>
          <td class="px-3 body-2" data-testid="step-filter-config__col-operator">
             {{ item.operator }}
          </td>
          <td class="px-3 body-2" data-testid="step-filter-config__col-filterword">
             {{ item.filterword }}
          </td>
          <td class="px-3 body-2" data-testid="step-filter-config__col-knockoff">
            {{ getKnockOffLabel(item) }}
          </td>
          <td class="px-3" data-testid="step-filter-config__col-actions">
            <div class="d-flex align-center gap-1">
              <v-tooltip bottom content-class="tooltip-bottom">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on" color="primary"
                    @click.stop="openEditDialog(item, i)"
                    data-testid="step-filter-config__edit-btn">
                    <v-icon small>mdi-pencil-outline</v-icon>
                  </v-btn>
                </template>
                <span>Edit</span>
              </v-tooltip>
              <v-tooltip bottom content-class="tooltip-bottom">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on" color="error"
                    @click.stop="promptDelete(item)"
                    data-testid="step-filter-config__delete-btn">
                    <img src="/img/icons/tm-icons/Trash.svg" alt="Delete" data-testid="step-filter-config__delete-icon"/>
                  </v-btn>
                </template>
                <span>Delete</span>
              </v-tooltip>
              <v-divider vertical class="mx-1" />
              <v-switch
                data-testid="step-filter-config__status-toggle"
                :input-value="isFilterEnabled(item)"
                :false-value="false"
                :true-value="true"
                :label="isFilterEnabled(item) ? 'Enabled' : 'Disabled'"
                @change="$emit('toggle-filter-status', item, $event)"
                inset
                dense
                color="primary"
                class="mt-0 pt-0"
                hide-details
              />
            </div>
          </td>
        </tr>
      </template>
    </lb-datatableaction>

    <!-- Delete Confirmation Dialog -->
    <confirm-delete-dialog
      v-model="showDeleteDialog"
      message="Are you sure you want to delete this filter?"
      sub-message="This action cannot be undone."
      data-testid="step-filter-config"
      @confirm="confirmDelete"
    />

    <!-- Create / Edit Filter Dialog -->
    <filter-form-dialog
      v-model="showDialog"
      :isEditing="isEditing"
      :loading="dialogLoading"
      :initialForm="form"
      :formErrors="formErrors"
      :fileTypeOptions="fileTypeOptions"
      :excludeTypes="excludeTypes"
      :tables="tables"
      :columnOptions="columnOptions"
      :operators="operators"
      @tableid-change="form.tableid = $event"
      @submit="onFilterDialogSubmit"
      @cancel="closeDialog"
    />

  </div>
</template>

<script>
import ConfirmDeleteDialog from './ConfirmDeleteDialog.vue';
import FilterFormDialog from './FilterFormDialog.vue';

export default {
  name: 'StepFilterConfigV2',

  components: { ConfirmDeleteDialog, FilterFormDialog },

  props: {
    formatedData: {
      type: Array,
      default: () => [],
    },
    headers: {
      type: Array,
      default: () => [],
    },
    setupList: {
      type: Object,
      default: () => ({}),
    },
    filtercolums: {
      type: Array,
      default: () => [],
    },
    operatorType: {
      type: Array,
      default: () => [],
    },
    filterrowType: {
      type: Array,
      default: () => ['Exclude', 'Delete'],
    },
    dialogLoading: {
      type: Boolean,
      default: false,
    },
    fileConfigInfo: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      showDialog: false,
      showDeleteDialog: false,
      deleteTarget: null,
      isEditing: false,
      editingItem: null,
      draggedItemIndex: null,
      isDirty: false,
      form: this.emptyForm(),
      formErrors: {},
      tableFilters: {},
    };
  },

  computed: {
    HEADERS() {
      return [
        { text: '',             value: 'drag',        visible: true, sortable: false, filterable: false, width: '36px' },
        { text: 'S.No',          value: 'indexno',     visible: true, sortable: false, filterable: false },
        { text: 'Dataset Combination', value: 'filetype',    visible: true, sortable: false, filterable: true  },
        { text: 'Tag',           value: 'tag',         visible: true, sortable: false, filterable: true  },
        { text: 'Description',   value: 'description', visible: true, sortable: false, filterable: true  },
        { text: 'Filter Action',   value: 'excludetype', visible: true, sortable: false, filterable: true  },
        { text: 'Select Table',        value: 'tableid',     visible: true, sortable: false, filterable: true  },
        { text: 'Select Column',       value: 'column',      visible: true, sortable: false, filterable: true  },
        { text: 'Filter Type',     value: 'operator',    visible: true, sortable: false, filterable: true  },
        { text: 'Filter Value', value: 'filterword',  visible: true, sortable: false, filterable: true  },
        { text: 'Knockoff Matching', value: 'isKnockOffFilter', visible: true, sortable: false, filterable: true },
        { text: 'Actions',      value: 'actions',     visible: true, sortable: false, filterable: false, align: 'center' },
      ];
    },

    tables() {
      return this.setupList?.file || [];
    },

    excludeTypes() {
      return this.filterrowType;
    },

    operators() {
      return this.operatorType;
    },

    columnOptions() {
      const tableId = this.form.tableid;
      if (!tableId || !this.fileConfigInfo) return this.filtercolums || [];
      // fileConfigInfo keys are fileconfig _ids; each entry has a .file property = the actual file _id
      const entry = this.fileConfigInfo[tableId]
        || Object.values(this.fileConfigInfo).find(c => c && (c.file === tableId || c._id === tableId));
      if (entry && entry.columnmap) {
        return entry.columnmap.map(col =>
          Array.isArray(col) ? col[1] : (col.label || col.name || col)
        ).filter(Boolean);
      }
      return this.filtercolums || [];
    },

    fileTypeOptions() {
      const result = [];
      const files = this.setupList?.file || [];
      for (let i = 0; i < files.length; i++) {
        for (let j = i + 1; j < files.length; j++) {
          result.push({
            name: `${files[i].name} vs ${files[j].name}`,
            value: `${files[i]._id}_vs_${files[j]._id}`,
          });
        }
      }
      return result;
    },

    // Replaces raw IDs with display names so column filter dropdowns show readable values.
    mappedTableData() {
      return this.formatedData.map(item => ({
        ...item,
        filetype: this.getItemFileTypeName(item),
        tableid:  this.getTableName(item.tableid),
        isKnockOffFilter: this.getKnockOffLabel(item),
      }));
    },

    filteredTableData() {
      const filters = this.tableFilters;
      if (!filters || Object.keys(filters).length === 0) return this.formatedData;
      // Filter raw items by comparing against the display-mapped values (same values the column filter shows)
      return this.formatedData.filter((rawItem, idx) => {
        const displayItem = this.mappedTableData[idx];
        return Object.entries(filters).every(([key, values]) => {
          if (!values || values.length === 0) return true;
          const cellValue = String(displayItem[key] !== undefined && displayItem[key] !== null ? displayItem[key] : '');
          return values.some(v => String(v) === cellValue);
        });
      });
    },

    hasActiveTableFilter() {
      return Object.values(this.tableFilters || {}).some(v => Array.isArray(v) ? v.length > 0 : !!v);
    },
  },




  methods: {
    isFilterEnabled(item) {
      // status can be boolean true or string 'active'; anything else = disabled
      return item.status === true || item.status === 'active';
    },

    isKnockOffEnabled(item) {
      return item.isKnockOffFilter === true || item.isKnockOffFilter === 'true';
    },

    getKnockOffLabel(item) {
      return this.isKnockOffEnabled(item) ? 'Yes' : 'No';
    },

    emptyForm() {
      return {
        description: '',
        tag: '',
        excludetype: '',
        filetype: '',
        tableid: '',
        column: '',
        operator: '',
        filterword: '',
        isKnockOffFilter: false,
      };
    },

    getTableName(id) {
      const file = (this.setupList?.file || []).find(f => f._id === id);
      return file ? file.name : id || '—';
    },

    getFileTypeName(filetype) {
      if (!filetype) return '—';
      const match = this.fileTypeOptions.find(o => o.value === filetype);
      return match ? match.name : '—';
    },

    getItemFileTypeName(item) {
      const filetype = item.filetype
        || (item.fileConfig1 && item.fileConfig2 ? `${item.fileConfig1}_vs_${item.fileConfig2}` : '');
      return this.getFileTypeName(filetype);
    },

    openCreateDialog() {
      this.isEditing = false;
      this.editingItem = null;
      this.form = this.emptyForm();
      this.formErrors = {};
      this.showDialog = true;
    },

    openEditDialog(item, index) {
      this.isEditing = true;
      this.editingItem = { ...item, _index: index };
      const savedColumn   = item.column   || '';
      // Derive filetype from fileConfig1+fileConfig2 if filetype is not stored directly
      const savedFiletype = item.filetype
        || (item.fileConfig1 && item.fileConfig2 ? `${item.fileConfig1}_vs_${item.fileConfig2}` : '');
      this.form = {
        description: item.description || '',
        tag:         item.tag         || '',
        excludetype: item.excludetype || '',
        filetype:    savedFiletype,
        tableid:     item.tableid     || '',
        column:      savedColumn,
        operator:    item.operator    || '',
        filterword:  item.filterword  || '',
        isKnockOffFilter: this.isKnockOffEnabled(item),
      };
      this.formErrors = {};
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
      this.form = this.emptyForm();
      this.formErrors = {};
    },

    validate() {
      const errors = {};
      if (!this.form.filetype)            errors.filetype    = 'Please select a dataset combination';
      if (!this.form.description?.trim()) errors.description = 'Description is required';
      if (!this.form.tag?.trim())         errors.tag         = 'Tag is required';
      if (!this.form.excludetype)         errors.excludetype = 'Please select a type';
      if (!this.form.tableid)             errors.tableid     = 'Table is required';
      if (!this.form.column)              errors.column      = 'Column is required';
      if (!this.form.operator)            errors.operator    = 'Operator is required';
      if (!this.form.filterword?.trim())  errors.filterword  = 'Filter value is required';
      this.formErrors = errors;
      return Object.keys(errors).length === 0;
    },

    onFilterDialogSubmit(formData) {
      this.form = { ...formData };
      this.submitForm();
    },

    submitForm() {
      if (!this.validate()) return;
      // Derive fileConfig1 and fileConfig2 from the combined filetype string
      const [fileConfig1 = '', fileConfig2 = ''] = (this.form.filetype || '').split('_vs_');
      const payload = { ...this.form, fileConfig1, fileConfig2 };
      if (this.isEditing) {
        this.$emit('update-filter-v2', { ...this.editingItem, ...payload });
      } else {
        this.$emit('add-filter-v2', { ...payload });
      }
      this.closeDialog();
    },

    promptDelete(item) {
      this.deleteTarget = item;
      this.showDeleteDialog = true;
    },

    confirmDelete() {
      if (this.deleteTarget !== null) {
        this.$emit('delete-filter-v2', this.deleteTarget);
        this.deleteTarget = null;
        this.showDeleteDialog = false;
      }
    },

    onDragStart(index) {
      this.draggedItemIndex = index;
    },
    onDropFilter(index) {
      this.$emit('reorder-filters', { from: this.draggedItemIndex, to: index });
      this.isDirty = true;
      this.draggedItemIndex = null;
    },
    onDragEnd() {
      this.draggedItemIndex = null;
    },

    saveFilters() {
      this.isDirty = false;
      this.$emit('save-order-v2');
    },

    filterData(filters) {
      this.tableFilters = filters || {};
    },

    clearFilter() {
      this.tableFilters = {};
    },

    clearTableFilter() {
      if (this.$refs.dataTableAction) {
        this.$refs.dataTableAction.clearfilter();
      } else {
        this.tableFilters = {};
      }
    },
  },

  beforeDestroy() {
    this.form = this.emptyForm();
    this.formErrors = {};
    this.showDialog = false;
    this.showDeleteDialog = false;
    this.deleteTarget = null;
    this.tableFilters = {};
  },
};
</script>

<style lang="scss" scoped>
@import './StepFilterConfigV2.scss';
</style>